import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";

export default function LeadsInbox({ user }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) fetchLeads();
  }, [user?.id]);

const fetchLeads = async () => {
  setLoading(true);
  const timeout = setTimeout(() => setLoading(false), 8000); // 8 second safety net
  try {
    // Step 1: get all QR code IDs belonging to this user
    const { data: userQRs, error: qrError } = await supabase
      .from("qr_codes")
      .select("id, name, code")
      .eq("user_id", user.id);

    if (qrError) throw qrError;
    if (!userQRs?.length) { setLeads([]); setLoading(false); return; }

    const qrIds = userQRs.map(q => q.id);
    const qrMap = {};
    userQRs.forEach(q => { qrMap[q.id] = q; qrMap[q.code] = q; });

    // Step 2: get leads using qr_id only
    const { data, error } = await supabase
      .from("qr_leads")
      .select("*")
      .in("qr_id", qrIds)
      .order("created_at", { ascending: false });

    if (error) throw error;

    // Enrich with QR name
    const enriched = (data || []).map(lead => ({
      ...lead,
      qr_codes: qrMap[lead.qr_id] || qrMap[lead.qr_code] || null
    }));

    setLeads(enriched);
  } catch (err) {
    console.error("Inbox Sync Error:", err.message);
  } finally {
    clearTimeout(timeout);
    setLoading(false);
  }
};

  const exportLeadsCSV = () => {
    if (!leads.length) return alert("No leads to export");
    const headers = ["Date", "Name", "Email", "Phone", "QR"];
    const rows = leads.map(l => [
      new Date(l.created_at).toLocaleDateString(),
      `"${l.name}"`,
      l.email,
      l.phone || "—",
      `"${l.qr_codes?.name || 'Deleted'}"`
    ]);
    const csv = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Leads_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (loading) return <div className="p-20 text-center text-white font-black uppercase tracking-[5px]">Accessing Records...</div>;

  return (
    <div className="p-8 bg-[#020617] min-h-screen text-white">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-black italic uppercase italic tracking-tighter">Private Leads</h1>
        <div className="flex gap-2">
          <button onClick={exportLeadsCSV} className="bg-blue-600 px-4 py-2 rounded-lg text-[10px] font-bold uppercase">Export Leads</button>
          <button 
            onClick={fetchLeads} 
            className="flex-1 md:flex-none bg-white text-black px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-500 hover:text-white transition-all shadow-xl active:scale-95"
          >
            Refresh Data
          </button>
        </div>
      </div>

      <div className="grid gap-4">
        {leads.length === 0 ? (
          <div className="p-20 text-center border border-white/10 rounded-[32px] text-slate-500">No leads found for your account.</div>
        ) : (
          leads.map((lead) => (
            <div key={lead.id} className="p-5 bg-white/5 border border-white/10 rounded-2xl flex justify-between items-center">
              <div>
                <p className="font-bold text-white">{lead.name}</p>
                <p className="text-xs text-slate-500">{lead.email}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-black text-blue-500 uppercase">{lead.qr_codes?.name}</p>
                <p className="text-[9px] text-slate-600">{new Date(lead.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}